import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable
} from 'react-beautiful-dnd';

import Constants from '../../constants';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Grid from '@mui/material/Unstable_Grid2';
import { IconButtonWithConfirmation } from '../../../shared/components/IconButtonWithConfirmation';
import RadioGroup from '@mui/material/RadioGroup';
import React from 'react';
import { SelectionComponent } from '../../models/LessonComponent';
import { SelectionItemEditor } from './SelectionItemEditor';
import { SelectionOption } from '../../models/LessonComponentContent';
import { cloneDeep } from 'lodash';
import { nanoid } from 'nanoid';
import { useLessonEditContext } from '../../models/LessonEditContext';

interface SingleSelectionEditorProps {
  component: SelectionComponent;
  hasImage?: boolean;
}

export const SingleSelectionEditor = (props: SingleSelectionEditorProps) => {
  const options = props.component.content.options || [];
  const isSurvey = props.component?.settings.survey;
  const showBorder = props.component?.settings.selectionSettings?.showBorder;
  const correctResponsePattern = isSurvey
    ? ''
    : props.component.content.correctResponsePattern || null;
  const { handleSettingsChange } = useLessonEditContext();

  const newItem: SelectionOption = {
    label: '',
    value: nanoid(),
    image: ''
  };

  const handleDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    const copyOptions = cloneDeep(options);
    const movingItem = copyOptions[fromIndex];
    copyOptions.splice(fromIndex, 1);
    copyOptions.splice(toIndex, 0, movingItem);
    const copy = cloneDeep(props.component);
    copy.content.options = copyOptions;
    handleSettingsChange(copy);
  };

  const handleOptionChange = (item: SelectionOption) => {
    const copyOptions = cloneDeep(options);
    const index = copyOptions.findIndex(
      (option) => option.value === item.value
    );
    copyOptions[index] = item;
    const copy = cloneDeep(props.component);
    copy.content.options = copyOptions;
    handleSettingsChange(copy);
  };

  const handleSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) {
      const correctResponse = event.target.value;
      const copy = cloneDeep(props.component);
      copy.content.correctResponsePattern = correctResponse;
      handleSettingsChange(copy);
    }
  };

  const handleDeleteOption = (id: string) => {
    const copyOptions = cloneDeep(options);
    const optionIndex = copyOptions.findIndex((o) => o.value === id);
    copyOptions.splice(optionIndex, 1);
    const copy = cloneDeep(props.component);
    /** set correctResponsePattern as null if the deleted option is the correctResponsePatttern  */
    if (copyOptions.length > 0) {
      if (correctResponsePattern === id) {
        copy.content.correctResponsePattern = copyOptions[0].value;
      }
    } else {
      copy.content.correctResponsePattern = null;
    }
    copy.content.options = copyOptions;
    handleSettingsChange(copy);
  };

  const handleAddOption = (item: SelectionOption) => {
    const copyOptions = cloneDeep(options);
    copyOptions.push(item);
    const copy = cloneDeep(props.component);
    copy.content.options = copyOptions;
    if (copyOptions.length > 0 && !correctResponsePattern) {
      copy.content.correctResponsePattern = copyOptions[0].value;
    }
    handleSettingsChange(copy);
  };

  const radioGroupName = `group-${props.component.componentkey}`;

  const getDroppableStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#eee' : 'transparent',
    height: '100%'
  });

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="selection-options">
          {(provided1, snapshot1) => (
            <div
              ref={provided1.innerRef}
              style={getDroppableStyle(snapshot1.isDraggingOver)}
            >
              <RadioGroup
                name={radioGroupName}
                value={correctResponsePattern}
                style={{ flex: 1 }}
              >
                {options?.map((option, index) => (
                  <Draggable
                    key={option.value}
                    draggableId={option.value}
                    index={index}
                  >
                    {(provided2, snapshot2) => (
                      <div
                        ref={provided2.innerRef}
                        {...provided2.draggableProps}
                      >
                        <Grid
                          container
                          alignItems="center"
                          style={{ width: '100%' }}
                        >
                          <SelectionItemEditor
                            componentKey={props.component.componentkey}
                            hasImage={props.hasImage}
                            selectionCardItem={option}
                            onItemChange={handleOptionChange}
                            onSelectionChange={
                              isSurvey ? undefined : handleSelectionChange
                            }
                            correctResponse={[correctResponsePattern]}
                            showBorder={showBorder}
                          />
                          <Grid
                            {...provided2.dragHandleProps}
                            style={{ paddingTop: '3px' }}
                          >
                            <span
                              style={{
                                color: 'rgba(0,0,0,0.54)' //* to make it look the same as the 'delete' icon button
                              }}
                            >
                              <DragIndicatorIcon />
                            </span>
                          </Grid>
                          <Grid>
                            <IconButtonWithConfirmation
                              size="small"
                              onConfirm={() => handleDeleteOption(option.value)}
                            >
                              <DeleteIcon />
                            </IconButtonWithConfirmation>
                          </Grid>
                        </Grid>
                      </div>
                    )}
                  </Draggable>
                ))}
              </RadioGroup>
              {provided1.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {/* Hide 'Click to add option' for selection card components when max number of options reach */}
      {(!props.hasImage ||
        (options.length < Constants.SelectionCardMaxNumItems &&
          props.hasImage)) && (
        <Grid
          container
          direction="row"
          alignItems="center"
          style={{ width: '100%' }}
        >
          <SelectionItemEditor
            componentKey={props.component.componentkey}
            hasImage={props.hasImage}
            selectionCardItem={newItem}
            onItemChange={handleAddOption}
            onSelectionChange={handleSelectionChange}
            showBorder={showBorder}
          />
        </Grid>
      )}
    </>
  );
};
